// Chat ↔ people identity bridge.
//
// /people answers a NON-MANAGER its peers WITHOUT email — the server's
// reduced projection (PeopleService, 2026-08-14) carries user_id + name +
// avatar only; the caller's own row stays full. Chat is keyed by email
// (member_email, author_email), so for an editor, an SMM or a client every
// peer was nameless: authors rendered as raw emails, the channel / DM pickers
// had nobody to pick, and @mentions inserted a display name the server could
// never resolve. A manager gets full rows and never sees any of it.
//
// The server stamps chat member rows with `user_id` (the /people id form).
// These functions join the two: a person's email comes from the member rows
// the caller can already read, and a person known only by id is picked as
// `uid:<id>` and resolved server-side (member_user_ids on create, user_id on
// add — restricted to members of the workspace).
//
// Each function is self-contained (frank serializes them one by one); the
// per-root cache follows peopleUtils' `root.__peopleUtilsCache` pattern.

// user id → member email, from root.channelMembers rows that carry user_id.
// Memoized per channelMembers array (every write replaces the array).
export function chatEmailByUserId(root) {
  const rows = root && root.channelMembers
  if (!Array.isArray(rows) || !rows.length) return new Map()
  let bag = root.__chatPeopleCache
  if (!bag || typeof bag.get !== 'function') {
    bag = new WeakMap()
    try {
      root.__chatPeopleCache = bag
    } catch (_) {}
  }
  let map = bag.get(rows)
  if (map) return map
  map = new Map()
  for (const r of rows) {
    if (r && r.user_id != null && r.member_email) {
      map.set(String(r.user_id), String(r.member_email).trim().toLowerCase())
    }
  }
  bag.set(rows, map)
  return map
}

// email → person, over root.people (rows that carry an email) PLUS every
// person a chat member row names by user_id. The chat twin of the shell's
// peopleByEmail — same shape, so the sidebar's DM titles and the
// internal/external split read it unchanged. Memoized per (people,
// channelMembers) pair. Called bare (`el.call('chatPeopleByEmail')`) it reads
// `this.state.root`.
export function chatPeopleByEmail(root) {
  const r =
    root && typeof root === 'object' && ('people' in root || 'channelMembers' in root)
      ? root
      : this && this.state && this.state.root
  if (!r) return {}
  const people = Array.isArray(r.people) ? r.people : []
  const rows = Array.isArray(r.channelMembers) ? r.channelMembers : []
  let bag = r.__chatPeopleCache
  if (!bag || typeof bag.get !== 'function') {
    bag = new WeakMap()
    try {
      r.__chatPeopleCache = bag
    } catch (_) {}
  }
  const hit = bag.get(people)
  if (hit && hit.rows === rows) return hit.map
  const map = Object.create(null)
  const byId = new Map()
  for (const p of people) {
    if (!p) continue
    if (p.email) map[String(p.email).trim().toLowerCase()] = p
    const id = p.userId ?? p.user_id
    if (id != null && id !== '') byId.set(String(id), p)
  }
  for (const m of rows) {
    if (!m || m.user_id == null || !m.member_email) continue
    const email = String(m.member_email).trim().toLowerCase()
    if (map[email]) continue
    const p = byId.get(String(m.user_id))
    if (p) map[email] = p.email ? p : { ...p, email }
  }
  bag.set(people, { rows, map })
  return map
}

// findPerson for chat: the shell's lookup first (exact + fuzzy over
// root.people / peopleAll), then the member-row bridge above. Accepts the
// shell's two call shapes: (email) with `this` an element, or (root, email).
export function chatFindPerson(rootOrEmail, maybeEmail) {
  const bare = typeof rootOrEmail === 'string' && maybeEmail === undefined
  const root = bare ? this && this.state && this.state.root : rootOrEmail
  const email = bare ? rootOrEmail : maybeEmail
  if (!root || !email) return null
  let p = null
  try {
    p = this && typeof this.call === 'function' ? this.call('findPerson', root, email) : null
  } catch (_) {
    p = null
  }
  if (p) return p
  const map =
    this && typeof this.call === 'function'
      ? this.call('chatPeopleByEmail', root)
      : chatPeopleByEmail(root)
  return (map && map[String(email).trim().toLowerCase()]) || null
}

// The people list with `email` filled in wherever a chat member row names the
// same user. Rows that already carry an email are returned untouched.
export function withChatEmails(root, people) {
  const list = Array.isArray(people) ? people : []
  const rows = root && root.channelMembers
  if (!Array.isArray(rows) || !rows.length) return list
  const byId = new Map()
  for (const r of rows) {
    if (r && r.user_id != null && r.member_email) {
      byId.set(String(r.user_id), String(r.member_email).trim().toLowerCase())
    }
  }
  if (!byId.size) return list
  return list.map((p) => {
    if (!p || p.email) return p
    const id = p.userId ?? p.user_id
    const email = id != null ? byId.get(String(id)) : null
    return email ? { ...p, email } : p
  })
}

// Does this server resolve members by user id? It stamps `user_id` on the
// member rows it answers; an older server never does — and it would ignore
// member_user_ids (a DM picked by id came out as a DM with yourself). The
// pickers offer email-less peers only when this is true.
export function chatServerKnowsUserIds(root) {
  const rows = root && root.channelMembers
  if (!Array.isArray(rows)) return false
  for (const r of rows) if (r && r.user_id != null) return true
  return false
}

// The people a member picker offers: the workspace's active members (the
// shell's activeMembers) minus the viewer, emails filled in from the member
// rows, and — only on a server that resolves user ids — the peers still known
// by id alone. Called as el.call('chatPickablePeople', root).
export function chatPickablePeople(root) {
  const r = root || (this && this.state && this.state.root)
  if (!r) return []
  const base =
    this && typeof this.call === 'function' ? this.call('activeMembers', r) : r.people || []
  const me = r.currentUserEmail ? String(r.currentUserEmail).trim().toLowerCase() : null
  const rows = Array.isArray(r.channelMembers) ? r.channelMembers : []
  const byId = new Map()
  let knowsIds = false
  for (const m of rows) {
    if (m && m.user_id != null) {
      knowsIds = true
      if (m.member_email) byId.set(String(m.user_id), String(m.member_email).trim().toLowerCase())
    }
  }
  const out = []
  for (const p of Array.isArray(base) ? base : []) {
    if (!p) continue
    const id = p.userId ?? p.user_id
    const email = p.email
      ? String(p.email).trim().toLowerCase()
      : id != null
        ? byId.get(String(id))
        : null
    if (email && email === me) continue
    if (email) out.push(p.email ? p : { ...p, email })
    else if (knowsIds && id != null && id !== '') out.push(p)
  }
  return out
}

// The picker's identity for a person: their email when known, else
// `uid:<user id>`; null when the row has neither.
export function chatPersonKey(person) {
  if (!person) return null
  if (person.email) return String(person.email).trim().toLowerCase()
  const id = person.userId ?? person.user_id
  return id != null && id !== '' ? 'uid:' + String(id) : null
}

// Picker keys → { emails, userIds } for createChannel / addMembersToChannel.
export function splitChatPersonKeys(keys) {
  const emails = []
  const userIds = []
  for (const k of Array.isArray(keys) ? keys : []) {
    if (!k) continue
    const s = String(k)
    if (s.startsWith('uid:')) {
      const id = s.slice(4)
      if (id && !userIds.includes(id)) userIds.push(id)
    } else if (!emails.includes(s)) emails.push(s)
  }
  return { emails, userIds }
}
